import { FC, useState } from "react";
import {
  Button,
  Popover,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  alpha,
  Tooltip,
} from "@mui/material";
import { observer } from "mobx-react";

import { BsPinFill } from "react-icons/bs";
import ChevronDownIcon from "../../../icons/ChevronDownIcon";
import { ColumnsIcon } from "../../../icons/ColumnsIcon";
import { PinIcon } from "../../../icons/PinIcon";

import { useUploadHistoryStore } from "../../../../hooks/useUploadHistoryStore";
import { UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS } from "../../../../types/claims";

const UploadHistoryColumnVisibilityButton: FC = observer(() => {
  const {
    isColumnVisible,
    toggleColumnVisibility,
    isColumnRequired,
    toggleRequiredColumn,
  } = useUploadHistoryStore();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const totalColumns = UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS.length;
  const visibleCount = UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS.filter(
    (col) => col.required || isColumnRequired(col.id) || isColumnVisible(col.id),
  ).length;
  const pinnedCount = UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS.filter(
    (col) => col.required || isColumnRequired(col.id),
  ).length;

  return (
    <>
      <Button
        variant="text"
        startIcon={
          <Box
            component="span"
            aria-hidden
            sx={{
              display: "inline-flex",
              "& svg": { width: 18, height: 18 },
            }}
          >
            <ColumnsIcon />
          </Box>
        }
        endIcon={<ChevronDownIcon />}
        onClick={handleClick}
        sx={(theme) => ({
          textTransform: "none",
          fontWeight: 400,
          minWidth: "auto",
          whiteSpace: "nowrap",
          backgroundColor: theme.palette.background.paper,
          height: "36px",
          width: "130px",
          px: 1.5,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        Columns
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              width: 300,
              maxHeight: 440,
              py: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 0.5, flexShrink: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Show/Hide Columns
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {visibleCount} of {totalColumns} shown &middot; {pinnedCount} pinned
          </Typography>
        </Box>

        <Divider sx={{ my: 1, flexShrink: 0 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0,
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          {UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS.map((column) => {
            // Pinning a column just marks it required: always visible and checked.
            const required = column.required || isColumnRequired(column.id);
            const canTogglePin = !column.mandatory;

            return (
              <Box
                key={column.id}
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  backgroundColor: required
                    ? alpha(theme.palette.custom.brandTeal, 0.08)
                    : "transparent",
                  "&:hover": {
                    backgroundColor: required
                      ? alpha(theme.palette.custom.brandTeal, 0.08)
                      : theme.palette.action.hover,
                  },
                  "&:hover .column-pin-affordance": {
                    visibility: "visible",
                  },
                })}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={required || isColumnVisible(column.id)}
                      disabled={required}
                      onChange={() => toggleColumnVisibility(column.id)}
                      sx={{ py: 0.25 }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {column.label}
                    </Typography>
                  }
                  sx={{ flex: "1 1 auto", mx: 0, py: 0.75 }}
                />

                {!canTogglePin ? (
                  <Tooltip arrow title="This column is required!">
                    <Box
                      sx={{
                        display: "flex",
                        flexShrink: 0,
                        color: "custom.brandTeal",
                        cursor: "pointer",
                      }}
                    >
                      <BsPinFill size={13} />
                    </Box>
                  </Tooltip>
                ) : (
                  <Box
                    className="column-pin-affordance"
                    role="button"
                    aria-label={
                      required
                        ? `Unpin ${column.label} column`
                        : `Pin ${column.label} column`
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleRequiredColumn(column.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                      color: required ? "custom.brandTeal" : "text.disabled",
                      visibility: required ? "visible" : "hidden",
                      cursor: "pointer",
                      "&:hover": {
                        color: required ? "text.disabled" : "custom.brandTeal",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexShrink: 0,
                        color: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      <BsPinFill size={13} />
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ mt: 1, mb: 1, flexShrink: 0 }} />

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            px: 2,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexShrink: 0,
              mt: "2px",
              color: "text.secondary",
            }}
          >
            <PinIcon width={14} height={14} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Pinned columns freeze to the left of the table when scrolling
            sideways.
          </Typography>
        </Box>
      </Popover>
    </>
  );
});

export default UploadHistoryColumnVisibilityButton;
